
















import { computed, defineComponent, ref } from "@vue/composition-api";
import { debounce } from "lodash";
import providers from "@/lib/api";
import { getNotificationsPermissions, isNotificationsSupported } from "@/lib/browserUtils";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { FormFields, FormValues } from "@/types";
import BaseForm from "@/components/BaseForm";
import BaseIcon from "@/components/Base/BaseIcon";
import BasePage from "@/components/BasePage";
import PageTitle from "@/components/PageTitle";

export default defineComponent({
  name: "PagePushNotificationsSettings",
  components: { BaseIcon, PageTitle, BasePage, BaseForm },
  setup() {
    const values = ref<FormValues>({
      calls: true,
      messages: true,
      reviews: true,
      newPosts: true,
      ...(config.featureGoLive ? { goingLive: true } : {}),
    });
    const loading = ref(true);
    const notificationPermission = ref(getNotificationsPermissions());

    const fields = computed<FormFields>(() => [
      {
        class: "__messages mt-0 mb-1",
        name: "messages",
        label: $t("Incoming messages"),
        type: "switch",
      },
      {
        class: "__calls mt-0 mb-1",
        name: "calls",
        label: $t("Incoming calls"),
        type: "switch",
      },
      {
        class: "__reviews mt-0 mb-1",
        name: "reviews",
        label: $t("Replies to comments"),
        type: "switch",
      },
      ...(config.featureGoLive
        ? [
            {
              class: "__going-live mt-0 mb-1",
              name: "goingLive",
              label: $t("Going live"),
              type: "switch",
            },
          ]
        : []),
      {
        class: "__new-posts mt-0 mb-1",
        name: "newPosts",
        label: $t("New Posts"),
        type: "switch",
      },
    ]);

    const save = debounce(async () => {
      await providers.PushNotificationsProvider.settingsUpdate({
        data: {
          settings: {
            calls: values.value.calls,
            messages: values.value.messages,
            reviews: values.value.reviews,
            posts: values.value.newPosts,
            ...(config.featureGoLive ? { livechats: values.value.goingLive } : {}),
          },
        },
      });
    }, 500);
    const handleInput = (newValues: FormValues) => {
      if (isNotificationsSupported() && notificationPermission.value !== "granted" && newValues.pushNotifications) {
        Notification.requestPermission().then(r => (notificationPermission.value = r));
      }
      values.value = newValues;
      save();
    };
    const init = async () => {
      try {
        const { settings } = await providers.PushNotificationsProvider.settingsFind();
        values.value = {
          calls: settings.calls,
          messages: settings.messages,
          reviews: settings.reviews,
          newPosts: settings.posts,
          ...(config.featureGoLive ? { goingLive: settings.livechats } : {}),
        };
      } finally {
        loading.value = false;
      }
    };
    init();

    return { notificationPermission, fields, values, loading, handleInput };
  },
});
